import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MessageSquarePlus, X, Bug, Sparkles, Megaphone, CheckCircle2, Loader2, XCircle, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatTimeAgo } from '@/lib/utils';
import { cn } from '@/lib/utils';

type FeedbackKind = 'BUG' | 'FEATURE' | 'FEEDBACK';
type FeedbackStatus = 'OPEN' | 'IN_PROGRESS' | 'IMPLEMENTED' | 'REJECTED';

interface FeedbackItem {
  id: string;
  kind: FeedbackKind;
  title: string | null;
  body: string;
  status: FeedbackStatus;
  adminResponse: string | null;
  respondedAt: string | null;
  createdAt: string;
  updatedAt: string;
  authorUserId: string;
  authorName: string | null;
}

interface FeedbackStats {
  OPEN: number;
  IN_PROGRESS: number;
  IMPLEMENTED: number;
  REJECTED: number;
}

const KIND_META: Record<FeedbackKind, { label: string; icon: typeof Bug; color: string }> = {
  BUG: { label: 'Bug', icon: Bug, color: 'text-destructive' },
  FEATURE: { label: 'Feature', icon: Sparkles, color: 'text-primary' },
  FEEDBACK: { label: 'Feedback', icon: Megaphone, color: 'text-warning' },
};

const STATUS_META: Record<FeedbackStatus, { label: string; tone: string }> = {
  OPEN: { label: 'Open', tone: 'bg-warning/15 text-warning' },
  IN_PROGRESS: { label: 'In progress', tone: 'bg-primary/15 text-primary' },
  IMPLEMENTED: { label: 'Implemented', tone: 'bg-success/15 text-success' },
  REJECTED: { label: 'Rejected', tone: 'bg-muted text-muted-foreground' },
};

export function FeedbackNotes() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const isAdmin = user?.role === 'ADMIN';

  const stats = useQuery<FeedbackStats>({
    queryKey: ['feedback-stats'],
    queryFn: () => api('/api/feedback/stats'),
    refetchInterval: 60_000,
  });

  // Admins see the # of open notes from all users; everyone else sees their own.
  const badgeCount = stats.data?.OPEN ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Open feedback notes"
        className="fixed bottom-6 right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-transform hover:scale-105 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <MessageSquarePlus className="h-5 w-5" />
        {badgeCount > 0 ? (
          <span className="absolute -top-1 -right-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground">
            {badgeCount > 99 ? '99+' : badgeCount}
          </span>
        ) : null}
      </button>

      {open ? <FeedbackPanel onClose={() => setOpen(false)} isAdmin={isAdmin} /> : null}
    </>
  );
}

function FeedbackPanel({ onClose, isAdmin }: { onClose: () => void; isAdmin: boolean }) {
  const qc = useQueryClient();
  const [scope, setScope] = useState<'all' | 'mine'>(isAdmin ? 'all' : 'mine');

  const list = useQuery<FeedbackItem[]>({
    queryKey: ['feedback', scope, isAdmin],
    queryFn: () => api(`/api/feedback?scope=${isAdmin ? scope : 'mine'}`),
    refetchOnWindowFocus: true,
  });

  // Esc closes
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['feedback'] });
    qc.invalidateQueries({ queryKey: ['feedback-stats'] });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-end bg-background/40 backdrop-blur-sm sm:items-center sm:justify-end sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <aside className="flex h-[85vh] w-full max-w-md flex-col overflow-hidden rounded-t-2xl border-t bg-card shadow-2xl sm:h-[80vh] sm:rounded-2xl sm:border animate-content-in">
        <header className="flex items-center justify-between border-b px-5 py-3">
          <div>
            <h2 className="text-base font-semibold">Feedback &amp; ideas</h2>
            <p className="text-[11px] text-muted-foreground">
              Bugs · feature requests · anything that helps.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        <NewNoteForm onCreated={refresh} />

        {isAdmin ? (
          <div className="flex gap-1 border-b px-5 py-2">
            <ScopeChip active={scope === 'all'} onClick={() => setScope('all')}>
              Team ({list.data?.length ?? '…'})
            </ScopeChip>
            <ScopeChip active={scope === 'mine'} onClick={() => setScope('mine')}>
              Mine
            </ScopeChip>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto px-5 py-3">
          {list.isLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (list.data?.length ?? 0) === 0 ? (
            <p className="py-8 text-center text-xs text-muted-foreground">
              Nothing yet. Be the first to report a bug or request a feature.
            </p>
          ) : (
            <ul className="space-y-3">
              {list.data!.map((n) => (
                <NoteCard key={n.id} note={n} isAdmin={isAdmin} onChanged={refresh} />
              ))}
            </ul>
          )}
        </div>
      </aside>
    </div>
  );
}

function ScopeChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'rounded-full px-3 py-1 text-xs font-medium transition-colors',
        active ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground hover:text-foreground',
      )}
    >
      {children}
    </button>
  );
}

function NewNoteForm({ onCreated }: { onCreated: () => void }) {
  const [kind, setKind] = useState<FeedbackKind>('FEEDBACK');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = useMutation({
    mutationFn: () =>
      api('/api/feedback', {
        method: 'POST',
        body: { kind, title: title.trim() || null, body: body.trim() },
      }),
    onSuccess: () => {
      setBody('');
      setTitle('');
      setKind('FEEDBACK');
      setError(null);
      onCreated();
    },
    onError: (e: unknown) => setError(e instanceof ApiError ? e.message : 'Could not save'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!body.trim()) {
          setError('Add some detail first');
          return;
        }
        submit.mutate();
      }}
      className="border-b px-5 py-3"
    >
      <div className="mb-2 flex gap-1">
        {(['BUG', 'FEATURE', 'FEEDBACK'] as const).map((k) => {
          const meta = KIND_META[k];
          const Icon = meta.icon;
          const active = kind === k;
          return (
            <button
              type="button"
              key={k}
              onClick={() => setKind(k)}
              className={cn(
                'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-medium transition-colors',
                active ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Icon className={cn('h-3 w-3', active && meta.color)} />
              {meta.label}
            </button>
          );
        })}
      </div>
      <Input
        placeholder="Short title (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="mb-2 h-9 text-sm"
      />
      <textarea
        placeholder="What happened? What would help?"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={3}
        className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      />
      {error ? <p className="mt-1 text-[11px] text-destructive">{error}</p> : null}
      <div className="mt-2 flex justify-end">
        <Button type="submit" size="sm" disabled={submit.isPending || !body.trim()}>
          {submit.isPending ? 'Posting…' : 'Post'}
        </Button>
      </div>
    </form>
  );
}

function NoteCard({ note, isAdmin, onChanged }: { note: FeedbackItem; isAdmin: boolean; onChanged: () => void }) {
  const { user } = useAuth();
  const isAuthor = user?.id === note.authorUserId;
  const meta = KIND_META[note.kind];
  const Icon = meta.icon;
  const status = STATUS_META[note.status];
  const [editing, setEditing] = useState(false);
  const [response, setResponse] = useState(note.adminResponse ?? '');
  const [busy, setBusy] = useState(false);

  const setStatus = async (s: FeedbackStatus) => {
    setBusy(true);
    try {
      await api(`/api/feedback/${note.id}`, {
        method: 'PATCH',
        body: { status: s, adminResponse: response.trim() || null },
      });
      setEditing(false);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!confirm('Delete this note?')) return;
    setBusy(true);
    try {
      await api(`/api/feedback/${note.id}`, { method: 'DELETE' });
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <li className="rounded-lg border bg-muted/30 p-3">
      <div className="flex items-start gap-2">
        <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', meta.color)} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={cn('rounded px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide', status.tone)}>
              {status.label}
            </span>
            <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
              {formatTimeAgo(note.createdAt)}
            </span>
          </div>
          {note.title ? <p className="mt-1 truncate text-sm font-medium">{note.title}</p> : null}
          <p className="mt-1 whitespace-pre-wrap break-words text-xs text-foreground">{note.body}</p>
          <p className="mt-1 text-[10px] text-muted-foreground">
            — {note.authorName ?? 'Unknown'}
          </p>
          {note.adminResponse ? (
            <div className="mt-2 rounded border-l-2 border-primary/60 bg-background/50 px-2 py-1.5">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-primary">Admin reply</p>
              <p className="mt-0.5 whitespace-pre-wrap text-xs">{note.adminResponse}</p>
            </div>
          ) : null}

          {isAdmin ? (
            <div className="mt-2">
              {editing ? (
                <div className="space-y-2">
                  <textarea
                    placeholder="Optional response (shown to the author)"
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    rows={2}
                    className="w-full resize-none rounded-md border border-input bg-background px-2 py-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  />
                  <div className="flex flex-wrap gap-1">
                    <button
                      type="button"
                      onClick={() => setStatus('IN_PROGRESS')}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md bg-primary/15 px-2 py-1 text-[11px] font-medium text-primary hover:bg-primary/25 disabled:opacity-50"
                    >
                      <Loader2 className="h-3 w-3" /> In progress
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus('IMPLEMENTED')}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md bg-success/15 px-2 py-1 text-[11px] font-medium text-success hover:bg-success/25 disabled:opacity-50"
                    >
                      <CheckCircle2 className="h-3 w-3" /> Implemented
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatus('REJECTED')}
                      disabled={busy}
                      className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground hover:bg-muted/80 disabled:opacity-50"
                    >
                      <XCircle className="h-3 w-3" /> Reject
                    </button>
                    <button
                      type="button"
                      onClick={() => setEditing(false)}
                      className="ml-auto text-[11px] text-muted-foreground hover:text-foreground"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-[11px] text-primary hover:underline"
                  >
                    Triage
                  </button>
                  <button
                    type="button"
                    onClick={remove}
                    disabled={busy}
                    className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              )}
            </div>
          ) : isAuthor && note.status === 'OPEN' ? (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className="mt-2 inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="h-3 w-3" /> Delete
            </button>
          ) : null}
        </div>
      </div>
    </li>
  );
}
